import { useEffect, useRef } from "react";
import { Text, TextField, Select } from "@radix-ui/themes";
import { useManagerContext } from "./context";
import { AutocompleteBase } from "./AutocompleteBase";
import { MultiAutocompleteBase } from "./MultiAutocompleteBase";

export type BaseFieldProps = {
  hideInEditMode?: boolean;
};

type TextFieldProps = BaseFieldProps & {
  name: string;
  label: string;
  required?: boolean;
  placeholder?: string;
  type?: "text" | "number";
  readOnly?: boolean;
  getValue?: (item: unknown) => string;
};

export function FormTextField({
  name,
  label,
  required,
  placeholder,
  type = "text",
  readOnly,
  getValue,
}: TextFieldProps) {
  const { formValues, setFormValue, selectedItem, dialogOpen, isEditMode } =
    useManagerContext();

  const getValueRef = useRef(getValue);
  getValueRef.current = getValue;

  // Sync with selected item when dialog opens in edit mode
  useEffect(() => {
    if (dialogOpen && isEditMode && selectedItem && getValueRef.current) {
      setFormValue(name, getValueRef.current(selectedItem));
    } else if (dialogOpen && !isEditMode) {
      setFormValue(name, "");
    }
  }, [dialogOpen, isEditMode, selectedItem, name, setFormValue]);

  const value = (formValues[name] as string) ?? "";

  return (
    <label>
      <Text as="div" size="2" mb="1" weight="bold">
        {label}
        {required && " *"}
      </Text>
      <TextField.Root
        type={type}
        value={value}
        onChange={(e) => setFormValue(name, e.target.value)}
        placeholder={placeholder}
        readOnly={readOnly}
      />
    </label>
  );
}

type SelectFieldProps<O> = BaseFieldProps & {
  name: string;
  label: string;
  options: O[];
  getOptionLabel: (option: O) => string;
  getOptionValue: (option: O) => string;
  getValue?: (item: unknown) => string | null;
  allowEmpty?: boolean;
  emptyLabel?: string;
  placeholder?: string;
  disabled?: boolean;
};

export function SelectField<O>({
  name,
  label,
  options,
  getOptionLabel,
  getOptionValue,
  getValue,
  allowEmpty = false,
  emptyLabel = "None",
  placeholder,
  disabled,
}: SelectFieldProps<O>) {
  const { formValues, setFormValue, selectedItem, dialogOpen, isEditMode } =
    useManagerContext();

  const getValueRef = useRef(getValue);
  getValueRef.current = getValue;

  // Sync with selected item when dialog opens in edit mode
  useEffect(() => {
    if (dialogOpen && isEditMode && selectedItem && getValueRef.current) {
      const val = getValueRef.current(selectedItem);
      setFormValue(name, val ?? (allowEmpty ? "__empty__" : ""));
    } else if (dialogOpen && !isEditMode) {
      setFormValue(name, allowEmpty ? "__empty__" : "");
    }
  }, [dialogOpen, isEditMode, selectedItem, name, setFormValue, allowEmpty]);

  const value = (formValues[name] as string) ?? (allowEmpty ? "__empty__" : "");

  return (
    <label>
      <Text as="div" size="2" mb="1" weight="bold">
        {label}
      </Text>
      <Select.Root
        value={value}
        onValueChange={(v) => setFormValue(name, v)}
        disabled={disabled}
      >
        <Select.Trigger placeholder={placeholder} style={{ width: "100%" }} />
        <Select.Content>
          {allowEmpty && (
            <Select.Item value="__empty__">{emptyLabel}</Select.Item>
          )}
          {options.map((option) => (
            <Select.Item
              key={getOptionValue(option)}
              value={getOptionValue(option)}
            >
              {getOptionLabel(option)}
            </Select.Item>
          ))}
        </Select.Content>
      </Select.Root>
    </label>
  );
}

type AutocompleteFieldProps<O> = BaseFieldProps & {
  name: string;
  label: string;
  placeholder?: string;
  readOnly?: boolean;
  getValue?: (item: unknown) => number | null;
  searchFn: (searchText: string, limit?: number) => Promise<O[]>;
  fetchByIdFn: (id: number) => Promise<O | null>;
  getOptionLabel: (option: O) => string;
  getOptionValue: (option: O) => number;
};

export function AutocompleteField<O>({
  name,
  label,
  placeholder,
  readOnly,
  getValue,
  searchFn,
  fetchByIdFn,
  getOptionLabel,
  getOptionValue,
}: AutocompleteFieldProps<O>) {
  const { formValues, setFormValue, selectedItem, dialogOpen, isEditMode } =
    useManagerContext();

  const getValueRef = useRef(getValue);
  getValueRef.current = getValue;

  const value = formValues[name] as number | null;

  // Sync with selected item when dialog opens in edit mode
  useEffect(() => {
    if (dialogOpen && isEditMode && selectedItem && getValueRef.current) {
      const val = getValueRef.current(selectedItem);
      setFormValue(name, val);
    } else if (dialogOpen && !isEditMode) {
      setFormValue(name, null);
    }
  }, [dialogOpen, isEditMode, selectedItem, name, setFormValue]);

  return (
    <AutocompleteBase<O>
      value={value}
      onChange={(v) => setFormValue(name, v)}
      label={label}
      labelWeight="bold"
      placeholder={placeholder}
      readOnly={readOnly}
      searchFn={searchFn}
      fetchByIdFn={fetchByIdFn}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
    />
  );
}

type DateFieldProps = BaseFieldProps & {
  name: string;
  label: string;
  required?: boolean;
  getValue?: (item: unknown) => string;
};

export function DateField({ name, label, required, getValue }: DateFieldProps) {
  const { formValues, setFormValue, selectedItem, dialogOpen, isEditMode } =
    useManagerContext();

  const getValueRef = useRef(getValue);
  getValueRef.current = getValue;

  // Sync with selected item when dialog opens in edit mode
  useEffect(() => {
    if (dialogOpen && isEditMode && selectedItem && getValueRef.current) {
      setFormValue(name, getValueRef.current(selectedItem));
    } else if (dialogOpen && !isEditMode) {
      setFormValue(name, "");
    }
  }, [dialogOpen, isEditMode, selectedItem, name, setFormValue]);

  const value = (formValues[name] as string) ?? "";

  return (
    <label>
      <Text as="div" size="2" mb="1" weight="bold">
        {label}
        {required && " *"}
      </Text>
      <TextField.Root
        type="date"
        value={value}
        onChange={(e) => setFormValue(name, e.target.value)}
        style={{ width: "fit-content" }}
      />
    </label>
  );
}

type MultiAutocompleteFieldProps<O> = BaseFieldProps & {
  name: string;
  label: string;
  placeholder?: string;
  readOnly?: boolean;
  getValue?: (item: unknown) => number[];
  searchFn: (searchText: string, limit?: number) => Promise<O[]>;
  fetchByIdsFn: (ids: number[]) => Promise<O[]>;
  getOptionLabel: (option: O) => string;
  getOptionValue: (option: O) => number;
};

export function MultiAutocompleteField<O>({
  name,
  label,
  placeholder,
  readOnly,
  getValue,
  searchFn,
  fetchByIdsFn,
  getOptionLabel,
  getOptionValue,
}: MultiAutocompleteFieldProps<O>) {
  const { formValues, setFormValue, selectedItem, dialogOpen, isEditMode } =
    useManagerContext();

  const getValueRef = useRef(getValue);
  getValueRef.current = getValue;

  const value = (formValues[name] as number[]) ?? [];

  // Sync with selected item when dialog opens in edit mode
  useEffect(() => {
    if (dialogOpen && isEditMode && selectedItem && getValueRef.current) {
      const val = getValueRef.current(selectedItem);
      setFormValue(name, val ?? []);
    } else if (dialogOpen && !isEditMode) {
      setFormValue(name, []);
    }
  }, [dialogOpen, isEditMode, selectedItem, name, setFormValue]);

  return (
    <MultiAutocompleteBase<O>
      value={value}
      onChange={(v) => setFormValue(name, v)}
      label={label}
      labelWeight="bold"
      placeholder={placeholder}
      readOnly={readOnly}
      searchFn={searchFn}
      fetchByIdsFn={fetchByIdsFn}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
    />
  );
}
